import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import NotificationBell from '@/components/NotificationBell';
import { axiosInstance } from '@/App';
import { 
  BookOpen, 
  AlertTriangle, 
  MessageSquare, 
  UserPlus, 
  FileText, 
  Award,
  Megaphone,
  LogOut,
  Beaker,
  Shield,
  ClipboardCheck,
  NotebookPen
} from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import useFeatureFlags from '@/hooks/useFeatureFlags';

const WorkerHome = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [badgeCounts, setBadgeCounts] = useState({
    messages: 0,
    jsas: 0,
    forms: 0,
    toolboxTalks: 0,
    nearMiss: 0,
    projectJhas: 0
  });

  // Fetch notification counts on mount
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Fetch unread messages count (use dedicated endpoint)
        let unreadMessages = 0;
        try {
          const messagesRes = await axiosInstance.get('/messages/unread-count');
          unreadMessages = messagesRes.data?.unreadCount || 0;
        } catch (e) {
          // Try fallback - get all messages and count unread
          try {
            const msgFallback = await axiosInstance.get('/messages');
            unreadMessages = msgFallback.data?.filter(m => !m.read)?.length || 0;
          } catch (e2) {
            console.warn('Unread-message count unavailable:', e2?.response?.status || e2.message);
          }
        }
        
        // Fetch unsigned JSAs count - workers see their assigned JSAs at /jsa
        let unsignedJsas = 0;
        try {
          const jsaRes = await axiosInstance.get('/jsa');
          // Count JSAs where worker hasn't acknowledged and JSA is pending signatures
          unsignedJsas = jsaRes.data?.filter(j => {
            // Check workerAcknowledgments (object with worker IDs as keys)
            const acknowledgments = j.workerAcknowledgments || {};
            const userAcknowledged = acknowledgments[user?.id]?.acknowledged === true;
            // Also check workerSignatures for backwards compatibility
            const signatures = j.workerSignatures || [];
            const userSigned = signatures.some(s => s.signedBy === user?.id || s.workerId === user?.id);
            const isActiveJsa = ['active', 'approved', 'pending', 'pending_signatures'].includes(j.status);
            return !userAcknowledged && !userSigned && isActiveJsa;
          })?.length || 0;
        } catch (e) {
          console.warn('JSA count unavailable:', e?.response?.status || e.message);
        }
        
        // Fetch pending forms count
        let pendingForms = 0;
        try {
          const formsRes = await axiosInstance.get('/worker/required-forms');
          pendingForms = formsRes.data?.filter(f => !f.completed)?.length || 0;
        } catch (e) {
          console.warn('Required-forms count unavailable:', e?.response?.status || e.message);
        }
        
        // Fetch unacknowledged toolbox talks
        let unacknowledgedTalks = 0;
        try {
          const talksRes = await axiosInstance.get('/toolbox-talks/assigned');
          unacknowledgedTalks = talksRes.data?.filter(t => !t.acknowledged)?.length || 0;
        } catch (e) {
          console.warn('Toolbox-talk count unavailable:', e?.response?.status || e.message);
        }
        
        // Fetch near miss reports count
        let nearMissCount = 0;
        try {
          const nearMissRes = await axiosInstance.get('/near-miss/my-reports');
          nearMissCount = nearMissRes.data?.length || 0;
        } catch (e) {
          console.warn('Near-miss count unavailable:', e?.response?.status || e.message);
        }
        
        // Project JHAs awaiting this worker's acknowledgment
        let pendingProjectJhas = 0;
        try {
          const pjRes = await axiosInstance.get('/worker/pre-construction-jhas');
          pendingProjectJhas = pjRes.data?.pendingCount || 0;
        } catch (e) {
          console.warn('Project-JHA count unavailable:', e?.response?.status || e.message);
        }

        setBadgeCounts({
          messages: unreadMessages,
          jsas: unsignedJsas,
          forms: pendingForms,
          toolboxTalks: unacknowledgedTalks,
          nearMiss: nearMissCount,
          projectJhas: pendingProjectJhas
        });
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      }
    };

    if (user?.id) {
      fetchCounts();
    }
  }, [user?.id]);

  const featureFlags = useFeatureFlags();
  const tiles = [
    ...(featureFlags.phase3CorrectiveActions ? [{
      title: t('correctiveWorkflow.myActions'),
      description: t('correctiveWorkflow.myActionsDescription'),
      icon: ClipboardCheck,
      path: '/my-corrective-actions',
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      badgeKey: null
    }] : []),
    {
      title: t('home.trainingTitle'),
      description: t('home.trainingDesc'),
      icon: BookOpen,
      path: '/worker/training',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      badgeKey: null
    },
    {
      title: t('home.nearMissTitle'),
      description: t('home.nearMissDesc'),
      icon: AlertTriangle,
      path: '/worker/near-miss',
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600',
      badgeKey: 'nearMiss',
      badgeLabel: 'submitted'
    },
    {
      title: t('home.messagesTitle'),
      description: t('home.messagesDesc'),
      icon: MessageSquare,
      path: '/worker/messages',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      badgeKey: 'messages'
    },
    {
      title: t('home.newHireTitle'),
      description: t('home.newHireDesc'),
      icon: UserPlus,
      path: '/worker/onboarding',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      badgeKey: 'forms'
    },
    {
      title: t('home.jsaTitle'),
      description: t('home.jsaDesc'),
      icon: FileText,
      path: '/worker/jsas',
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      badgeKey: 'jsas'
    },
    {
      title: 'Project JHAs',
      description: 'Review and sign the approved project JHA',
      icon: ClipboardCheck,
      path: '/worker/project-jhas',
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      badgeKey: 'projectJhas'
    },
    {
      title: t('home.certificatesTitle'),
      description: t('home.certificatesDesc'),
      icon: Award,
      path: '/worker/certificates',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      badgeKey: null
    },
    {
      title: t('home.toolboxTitle'),
      description: t('home.toolboxDesc'),
      icon: Megaphone,
      path: '/worker/toolbox-talks',
      color: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      badgeKey: 'toolboxTalks'
    },
    {
      title: t('home.sdsTitle'),
      description: t('home.sdsDesc'),
      icon: Beaker,
      path: '/worker/sds-lookup',
      color: 'bg-cyan-500',
      hoverColor: 'hover:bg-cyan-600',
      badgeKey: null
    },
    {
      title: t('home.inspectionsTitle'),
      description: t('home.inspectionsDesc'),
      icon: ClipboardCheck,
      path: '/worker/inspections',
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      badgeKey: null
    },
    {
      title: t('home.safetyAssistantTitle'),
      description: t('home.safetyAssistantDesc'),
      icon: Shield,
      path: '/safety-assistant',
      color: 'bg-gradient-to-br from-blue-500 to-purple-600',
      hoverColor: 'hover:from-blue-600 hover:to-purple-700',
      badgeKey: null
    },
    // Field Notes tile - only visible to Service Trucks
    ...(user?.role === 'service_worker' ? [{
      title: t('home.fieldNotesTitle'),
      description: t('home.fieldNotesDesc'),
      icon: NotebookPen,
      path: '/worker/field-logs',
      color: 'bg-amber-600',
      hoverColor: 'hover:bg-amber-700',
      badgeKey: null
    }] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-xl">SM</span>
            </div>
            <div className="text-left">
              <h2 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">Safety</span>
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Mind</span>
                <span className="text-blue-600"> AI</span>
              </h2>
              <p className="text-sm text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('home.welcomeWorker')}, {user?.name || ''}
            </h1>
            <p className="text-gray-600 mt-2">{user?.companyName || t('home.quickActions')}</p>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <NotificationBell />
            <Button
              onClick={onLogout}
              variant="outline"
              className="flex items-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
              data-testid="worker-home-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </Button>
          </div>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            const badgeCount = tile.badgeKey ? badgeCounts[tile.badgeKey] : 0;
            const isNearMiss = tile.badgeKey === 'nearMiss';
            
            return (
              <Card
                key={tile.path}
                className={`cursor-pointer transition-all duration-200 transform hover:scale-105 hover:shadow-xl relative`}
                onClick={() => navigate(tile.path)}
              >
                {/* Notification Badge - Red for action needed, Green for near miss count */}
                {badgeCount > 0 && (
                  <div className="absolute -top-2 -right-2 z-10">
                    <Badge className={`${isNearMiss ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white text-xs font-bold min-w-[24px] h-6 flex items-center justify-center rounded-full shadow-lg ${isNearMiss ? '' : 'animate-pulse'}`}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <div className={`${tile.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {tile.description}
                  </p>
                  {/* Near miss shows "X reports submitted" */}
                  {isNearMiss && badgeCount > 0 && (
                    <p className="text-xs text-green-600 mt-2 font-medium">
                      {badgeCount} report{badgeCount > 1 ? 's' : ''} submitted
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkerHome;
